import React, { useState, useRef } from 'react';
import { FaTimes, FaEdit, FaCheck } from 'react-icons/fa';
import { useCart } from '../../context/CartContext';
import StandardImage from '../common/StandardImage';

const CartItem = ({ item }) => {
  const { removeItem, addOrUpdateItem } = useCart();
  const [isEditing, setIsEditing] = useState(false);
  const [quantity, setQuantity] = useState(item.bulkQuantity);
  const [error, setError] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const inputRef = useRef(null);

  const moq = item.moq || 200; // MOQ por defecto si no existe

  const handleRemove = () => {
    removeItem(item.id);
  };

  const handleEdit = () => {
    setIsEditing(true);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    }, 10);
  };

  // Ya no necesitamos validación compleja ya que solo permitimos valores predefinidos
  const handleQuantityChange = (e) => {
    const value = e.target.value;
    setQuantity(value);
    setError(''); // Siempre limpiamos el error ya que todas las opciones son válidas
  };

  const handleSave = async () => {
    // Ya no necesitamos validar el valor porque siempre será uno de los valores permitidos
    const numQuantity = parseInt(quantity, 10);
    
    console.log("Actualizando item en carrito:", {
      itemId: item.id,
      itemName: item.name,
      newQuantity: numQuantity,
      format: item.selectedFormat
    });
    
    // Mostrar estado de actualización
    setIsUpdating(true);
    
    try {
      // Preservar el formato seleccionado al actualizar la cantidad
      await addOrUpdateItem(item, numQuantity, item.selectedFormat);
      setIsEditing(false);
    } catch (error) {
      console.error("Error al actualizar la cantidad:", error);
      setError("Hubo un error al actualizar la cantidad");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancel = () => {
    setQuantity(item.bulkQuantity);
    setError('');
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  return (
    <div className="card border-0 shadow-sm">
      <div className="card-body">
        <div className="d-flex gap-3">
          {/* Imagen */}
          {item.image?.url && (
            <div style={{ width: '80px', height: '80px' }}>
              <StandardImage
                image={item.image}
                type="thumbnail"
                alt={item.name}
                className="img-fluid rounded"
                containerClassName="w-100 h-100"
              />
            </div>
          )}
          
          {/* Información */}
          <div className="flex-grow-1">
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <h6 className="mb-1">{item.name}</h6>
                {item.categoryName && (
                  <small className="text-muted d-block">
                    {item.categoryName}
                  </small>
                )}
                {item.selectedFormat && (
                  <small className="text-muted d-block">
                    <span className="fw-bold">Formato:</span> {item.selectedFormat.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </small>
                )}
                {item.countPerBottle && Array.isArray(item.countPerBottle) && item.countPerBottle.length > 0 && (
                  <small className="text-muted d-block">
                    <span className="fw-bold">Cantidad por frasco:</span> {item.countPerBottle[0].replace(/ct$/i, '')} pastillas
                  </small>
                )}
                {item.code && (
                  <small className="text-muted d-block">
                    <span className="fw-bold">SKU:</span> {item.code}
                  </small>
                )}
              </div>
              <button
                className="btn btn-link text-danger p-0"
                onClick={handleRemove}
                aria-label="Eliminar"
              >
                <FaTimes />
              </button>
            </div>
            
            {/* Cantidad */}
            <div className="mt-2">
              {isEditing ? (
                <div>
                  <div className="d-flex flex-column gap-2">
                    <div className="input-group input-group-sm">
                      <select
                        ref={inputRef}
                        className={`form-select ${error ? 'is-invalid' : ''}`}
                        value={quantity}
                        onChange={(e) => {
                          setQuantity(e.target.value);
                          setError('');
                        }}
                        onKeyDown={handleKeyDown}
                        style={{ minWidth: '100px' }}
                      >
                        <option value="200">200</option>
                        <option value="500">500</option>
                        <option value="1000">1000</option>
                      </select>
                      <span className="input-group-text">unidades</span>
                    </div>
                    <div className="d-flex gap-2">
                      <button 
                        className="btn btn-sm btn-success flex-grow-1" 
                        onClick={handleSave}
                        disabled={!!error || isUpdating}
                        aria-label="Guardar"
                      >
                        {isUpdating ? (
                          <span className="spinner-border spinner-border-sm" />
                        ) : (
                          <><FaCheck className="me-1" /> Confirmar</>
                        )}
                      </button>
                      <button 
                        className="btn btn-sm btn-secondary" 
                        onClick={handleCancel}
                        aria-label="Cancelar"
                      >
                        <FaTimes />
                      </button>
                    </div>
                  </div>
                  {error && <div className="invalid-feedback d-block">{error}</div>}
                  <div className="mt-1 small text-muted">
                    MOQ: {moq} unidades
                  </div>
                </div>
              ) : (
                <div className="d-flex align-items-center">
                  <span className="badge bg-primary me-2">
                    {item.bulkQuantity} unidades
                  </span>
                  <button
                    className="btn btn-link btn-sm p-0 text-primary"
                    onClick={handleEdit}
                    aria-label="Editar cantidad"
                  >
                    <FaEdit size={14} />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartItem;
